import { WX_ID_LFM } from "../../../store/WeatherContentsState";
import LfmUpper from "./LfmUpper";
import LfmGround from "./LfmGround";
import { _orderBy, calcPressureFromAlt, ft2m, translation } from "../../../utils/FPEUtils";
import LegendLfm from "../legend/LegendLfm";
import TimeController from "../helper/TimeController";
import Mutex from "../../../utils/Mutex";
import LegendLfmSub from "../legend/LegendLfmSub";

class Lfm {
  static NAME = WX_ID_LFM;
  static LFM_GROUND = `${Lfm.NAME}_Ground`;
  static LFM_UPPER = `${Lfm.NAME}_Upper`;
  static WIND_ARROW_NAME = `${Lfm.NAME}_WIND_ARROW`;
  static WIND_STREAM_NAME = `${Lfm.NAME}_WIND_STREAM`;
  static ISOTACH_CONTOUR_NAME = `${Lfm.NAME}_ISOTACH_CONTOUR`;
  static ISOTACH_GRADATION_NAME = `${Lfm.NAME}_ISOTACH_GRADATION`;
  static ISOTHERM_CONTOUR_NAME = `${Lfm.NAME}_ISOTHERM_CONTOUR`;
  static ISOTHERM_GRADATION_NAME = `${Lfm.NAME}_ISOTHERM_GRADATION`;
  static ISOTHERM_GRID_NAME = `${Lfm.NAME}_ISOTHERM_VALUE`;
  static GEO_POTENTIAL_HEIGHT_NAME = `${Lfm.NAME}_GEO_POTENTIAL_HEIGHT`;
  static ICING_PROB_NAME = `${Lfm.NAME}_ICING_PROB_NAME`;
  static PRECIPITATION_1H_MESH_NAME = `${Lfm.NAME}_PRECIPITATION_1H_MESH`;
  static PRECIPITATION_1H_CONTOUR_NAME = `${Lfm.NAME}_PRECIPITATION_1H_CONTOUR`;
  static PRECIPITATION_1H_GRID_NAME = `${Lfm.NAME}_PRECIPITATION_1H_GRID`;
  static RELATIVE_HUMIDITY_CONTOUR_NAME = `${Lfm.NAME}_RELATIVE_HUMIDITY_CONTOUR_NAME`;
  static RELATIVE_HUMIDITY_GRADATION_NAME = `${Lfm.NAME}_RELATIVE_HUMIDITY_GRADATION_NAME`;
  static LFM_UPPER_LEVEL = {
    "100": "FL530 / 100hPa",
    "150": "FL450 / 150hPa",
    "200": "FL390 / 200hPa",
    "250": "FL340 / 250hPa",
    "300": "FL300 / 300hPa",
    "400": "FL240 / 400hPa",
    "500": "FL180 / 500hPa",
    "600": "FL140 / 600hPa",
    "700": "FL100 / 700hPa",
    "800": "FL070 / 800hPa",
    "850": "FL050 / 850hPa",
    "900": "FL030 / 900hPa",
    "925": "FL025 / 925hPa",
    "950": "FL020 / 950hPa",
    "975": "FL010 / 975hPa",
    "1000": "FL005 / 1000hPa",
  };
  static LFM_GROUND_CONTENTS_KEYS = [
    Lfm.WIND_ARROW_NAME,
    Lfm.WIND_STREAM_NAME,
    Lfm.ISOTACH_CONTOUR_NAME,
    Lfm.ISOTACH_GRADATION_NAME,
    Lfm.ISOTHERM_CONTOUR_NAME,
    Lfm.ISOTHERM_GRADATION_NAME,
    Lfm.ISOTHERM_GRID_NAME,
    Lfm.ICING_PROB_NAME,
    Lfm.PRECIPITATION_1H_MESH_NAME,
    Lfm.PRECIPITATION_1H_CONTOUR_NAME,
    Lfm.PRECIPITATION_1H_GRID_NAME,
  ];
  static LFM_UPPER_CONTENTS_KEYS = [
    Lfm.WIND_ARROW_NAME,
    Lfm.WIND_STREAM_NAME,
    Lfm.ISOTACH_CONTOUR_NAME,
    Lfm.ISOTACH_GRADATION_NAME,
    Lfm.ISOTHERM_CONTOUR_NAME,
    Lfm.ISOTHERM_GRADATION_NAME,
    Lfm.ISOTHERM_GRID_NAME,
    Lfm.ICING_PROB_NAME,
    Lfm.GEO_POTENTIAL_HEIGHT_NAME,
    Lfm.RELATIVE_HUMIDITY_CONTOUR_NAME,
    Lfm.RELATIVE_HUMIDITY_GRADATION_NAME,
  ];
  static CONTENTS_VISIBLE_STATE_DEFAULT = {
    [Lfm.WIND_ARROW_NAME]: false,
    [Lfm.WIND_STREAM_NAME]: true,
    [Lfm.ISOTACH_CONTOUR_NAME]: true,
    [Lfm.ISOTACH_GRADATION_NAME]: false,
    [Lfm.ISOTHERM_CONTOUR_NAME]: true,
    [Lfm.ISOTHERM_GRADATION_NAME]: false,
    [Lfm.ISOTHERM_GRID_NAME]: false,
    [Lfm.GEO_POTENTIAL_HEIGHT_NAME]: true,
    [Lfm.ICING_PROB_NAME]: true,
    [Lfm.PRECIPITATION_1H_MESH_NAME]: true,
    [Lfm.PRECIPITATION_1H_CONTOUR_NAME]: true,
    [Lfm.PRECIPITATION_1H_GRID_NAME]: false,
    [Lfm.RELATIVE_HUMIDITY_CONTOUR_NAME]: true,
    [Lfm.RELATIVE_HUMIDITY_GRADATION_NAME]: false,
  };
  keyMap =  {
    [Lfm.WIND_ARROW_NAME]: {
      groundKey: LfmGround.WIND_ARROW_NAME,
      upperKey: LfmUpper.WIND_ARROW_NAME
    },
    [Lfm.WIND_STREAM_NAME]: {
      groundKey: LfmGround.WIND_STREAM_NAME,
      upperKey: LfmUpper.WIND_STREAM_NAME
    },
    [Lfm.ISOTACH_CONTOUR_NAME]: {
      groundKey: LfmGround.ISOTACH_CONTOUR_NAME,
      upperKey: LfmUpper.ISOTACH_CONTOUR_NAME
    },
    [Lfm.ISOTACH_GRADATION_NAME]: {
      groundKey: LfmGround.ISOTACH_GRADATION_NAME,
      upperKey: LfmUpper.ISOTACH_GRADATION_NAME
    },
    [Lfm.ISOTHERM_CONTOUR_NAME]: {
      groundKey: LfmGround.ISOTHERM_CONTOUR_NAME,
      upperKey: LfmUpper.ISOTHERM_CONTOUR_NAME
    },
    [Lfm.ISOTHERM_GRADATION_NAME]: {
      groundKey: LfmGround.ISOTHERM_GRADATION_NAME,
      upperKey: LfmUpper.ISOTHERM_GRADATION_NAME
    },
    [Lfm.ISOTHERM_GRID_NAME]: {
      groundKey: LfmGround.ISOTHERM_GRID_NAME,
      upperKey: LfmUpper.ISOTHERM_GRID_NAME
    },
    [Lfm.GEO_POTENTIAL_HEIGHT_NAME]: {
      groundKey: undefined,
      upperKey: LfmUpper.GEO_POTENTIAL_HEIGHT_NAME
    },
    [Lfm.ICING_PROB_NAME]: {
      groundKey: LfmGround.ICING_PROB_NAME,
      upperKey: LfmUpper.ICING_PROB_NAME
    },
    [Lfm.PRECIPITATION_1H_MESH_NAME]: {
      groundKey: LfmGround.PRECIPITATION_1H_MESH_NAME,
      upperKey: undefined
    },
    [Lfm.PRECIPITATION_1H_CONTOUR_NAME]: {
      groundKey: LfmGround.PRECIPITATION_1H_CONTOUR_NAME,
      upperKey: undefined
    },
    [Lfm.PRECIPITATION_1H_GRID_NAME]: {
      groundKey: LfmGround.PRECIPITATION_1H_GRID_NAME,
      upperKey: undefined
    },
    [Lfm.RELATIVE_HUMIDITY_CONTOUR_NAME]: {
      groundKey: undefined,
      upperKey: LfmUpper.RELATIVE_HUMIDITY_CONTOUR_NAME
    },
    [Lfm.RELATIVE_HUMIDITY_GRADATION_NAME]: {
      groundKey: undefined,
      upperKey: LfmUpper.RELATIVE_HUMIDITY_GRADATION_NAME
    },
  };
  viewGround = true;
  altitude = 0;
  level = '0';
  contentsVisibleState = {...Lfm.CONTENTS_VISIBLE_STATE_DEFAULT};
  //LFMタイムコントローラー
  lfmTimeController;
  //タイムコントローラーのリスト
  timeList;
  //自動更新フラグ
  autoRefresh = true;

  constructor(isDark) {
    this.lfmTimeController = new TimeController(WX_ID_LFM,{
      label: 'LFM',
      getTimeList: () => {
        if (this.timeList) {
          return this.timeList;
        }
        this._updateTimeList();
        return this.timeList;
      },
      updated: () => {
        this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
      },
      changeAutoRefresh: (autoFlg) => {
        this.setAutoRefresh(autoFlg);
      }
    });
    this.lfmUpper = new LfmUpper(isDark, this.lfmTimeController);
    this.lfmGround = new LfmGround(isDark, this.lfmTimeController);
    this.lfmUpper.setUpdateTimeFunc(this._updateTimeList.bind(this));
    this.lfmGround.setUpdateTimeFunc(this._updateTimeList.bind(this));
    this.drawLayerMutex = new Mutex();
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
    const cascade = (action) => {
      if (action.type === 'SET_CONTENTS_TIME') {
        this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
      } else {
        return this.dispatcher(action);
      }
    };
    this.lfmUpper.setDispatcher(cascade);
    this.lfmGround.setDispatcher(cascade);
  }

  setModelDispatcher(dispatcher) {
    this.modelDispatcher = dispatcher;
  }

  async drawLayer(visible) {
    this.drawLayerMutex.runExclusion(async () => {
      this.visible = visible;

      if (visible) {
        //visible = falseの切り替えから行う(TimeController絡みの最適化）
        if(this.viewGround) {
          await Promise.all([
            this.lfmUpper.drawLayer(false),
            this.lfmGround.drawLayer(true)
          ]);
        } else {
          await Promise.all([
            this.lfmGround.drawLayer(false),
            this.lfmUpper.drawLayer(true)
          ]);
        }
      } else {
        await Promise.all([
          this.lfmGround.drawLayer(false),
          this.lfmUpper.drawLayer(false)
        ]);
      }
    });
  }

  getLayer() {
    return [...this.lfmUpper.getLayer(), ...this.lfmGround.getLayer()];
  }

  getTimeInformation() {
    let info;
    if(this.viewGround) {
      info = this.lfmGround.getTimeInformation();
    } else {
      info = this.lfmUpper.getTimeInformation();
    }
    info.id = Lfm.NAME;
    return info;
  }

  getLegend() {
    return {
      id: Lfm.NAME,
      label: 'LFM',
      component: LegendLfm,
      subComponent: LegendLfmSub,
      contents: this
    };
  }

  getContentsVisibleState() {
    return this.contentsVisibleState;
  }

  getIndividualAltitude() {
    return {
      id: Lfm.NAME,
      altitude: this.altitude,
      label: 'LFM',
      tooltip: this._altitudeTooltip
    };
  }

  getAltitudeLabel() {
    return this._altitudeTooltip(this.altitude);
  }

  applyIndividualAltitude(altitude) {
    if(this.altitude !== altitude) {
      const before = this.viewGround;
      if(altitude === 0) {
        this.viewGround = true;
      } else {
        this.viewGround = false;
        this.lfmUpper.applyIndividualAltitude(altitude);
      }

      if (before !== this.viewGround) {
        this.drawLayer(this.visible);
        this._updateTimeList();
      }

      this.altitude = altitude;
    }
  }

  applyElapsed(elapsed) {
    this.lfmGround.applyElapsed(elapsed);
    this.lfmUpper.applyElapsed(elapsed);
  }

  applyPlanETD(etd) {
    this.lfmGround.applyPlanETD(etd);
    this.lfmUpper.applyPlanETD(etd);
  }

  getTimeController() {
    return this.lfmTimeController;
  }

  setAutoRefresh(autoFlg) {
    this.autoRefresh = autoFlg;
    this.lfmGround.setAutoRefresh(autoFlg);
    this.lfmUpper.setAutoRefresh(autoFlg);
  }

  _altitudeTooltip(altitude) {
    if(altitude === 0) return translation('Ground');
    const lfm = [
      {fl: 530, mb: 100},
      {fl: 450, mb: 150},
      {fl: 390, mb: 200},
      {fl: 340, mb: 250},
      {fl: 300, mb: 300},
      {fl: 240, mb: 400},
      {fl: 180, mb: 500},
      {fl: 140, mb: 600},
      {fl: 100, mb: 700},
      {fl: 70, mb: 800},
      {fl: 50, mb: 850},
      {fl: 30, mb: 900},
      {fl: 25, mb: 925},
      {fl: 20, mb: 950},
      {fl: 10, mb: 975},
      {fl: 5, mb: 1000},
    ]

    const m = ft2m(altitude * 100);
    const mb = calcPressureFromAlt(m);
    const near = _orderBy(lfm, [
      o => {
        return Math.abs(mb - o.mb);
      },
      o => {
        return o;
      }
    ])[0];
    const fl = ('000' + near.fl).slice(-3);
    return `FL${fl} / ${near.mb}hPa`;
  }

  getDataType() {
    return this.viewGround ? Lfm.LFM_GROUND : Lfm.LFM_UPPER;
  }

  getContentsKeys() {
    return this.viewGround ? Lfm.LFM_GROUND_CONTENTS_KEYS : Lfm.LFM_UPPER_CONTENTS_KEYS;
  }

  resetContentsVisibleState(csAllOff) {
    if (Object.keys(this.contentsVisibleState)
      .filter(key => this.getContentsKeys(this.getDataType()).includes(key))
      .filter(key => this.contentsVisibleState[key]).length === 0 && csAllOff) {
      this.contentsVisibleState = {...Lfm.CONTENTS_VISIBLE_STATE_DEFAULT};
      this.lfmGround.resetContentsVisibleState();
      this.lfmUpper.resetContentsVisibleState();
      return true;
    }
    return false;
  }

  applyMapColor(mapColor, isDark) {
    this.lfmGround.applyMapColor(mapColor, isDark);
    this.lfmUpper.applyMapColor(mapColor, isDark);
  }

  async setVisible(visible, contents) {
    if (this.contentsVisibleState.hasOwnProperty(contents)) {
      this.contentsVisibleState[contents] = visible;

      const key = this.keyMap[contents];
      if (key?.groundKey !== undefined) {
        this.lfmGround.setVisible(visible, key.groundKey);
      }
      if (key?.upperKey !== undefined) {
        this.lfmUpper.setVisible(visible, key.upperKey);
      }
    }
    await this.modelDispatcher({type: 'SET_MODEL_CONTENTS_VISIBLE', payload: {targetName: contents, visible}});
  }

  _updateTimeList() {
    let timelist;
    if (this.viewGround) {
      timelist = this.lfmGround?.dh?.retained?.timelist?.timelist;
    } else {
      timelist = this.lfmUpper?.dh?.retained?.timelist?.timelist;
    }

    if (!timelist) {
      return;
    }

    //最新のbasetimeのvalidtimeを取得
    const latestBasetime = _orderBy(timelist, ['basetime'], ['desc'])[0];
    if (!latestBasetime.validtime) {
      return;
    }
    this.timeList = Array.from(latestBasetime.validtime).sort();

    if (this.autoRefresh) {
      if (this.viewGround) {
        this.lfmTimeController.setCurrentTime(this.lfmGround.validTime);
      } else {
        this.lfmTimeController.setCurrentTime(this.lfmUpper.validTime);
      }
    }
    this.lfmTimeController.adjustCurrentTime();
    this.lfmTimeController.invalidate();
  }

  getLevels() {
    const labels = Object.keys(Lfm.LFM_UPPER_LEVEL)
      .sort((a,b) => { return a - b;})
      .map(value => {
        return {
          value,
          label: Lfm.LFM_UPPER_LEVEL[value]
        };
      });
    labels.push({value: "0", label: translation('Ground')});
    return labels;
  }
  async setLevel(level) {
    this.modelDispatcher({type: 'SET_MODEL_LEVEL', payload: level});

    if (this.level === level) return;
    this.level = level;
    await this.setViewGround(level === "0");
    if (level !== "0") {
      this.lfmUpper.applyIndividualLevel(level, Lfm.LFM_UPPER_LEVEL[level]);
    }
  }
  getLevel() {
    return this.level;
  }
  async setViewGround(viewGround) {
    if (this.viewGround === viewGround) return;
    this.viewGround = viewGround;
    await this.drawLayer(this.visible);
    this._updateTimeList();
  }

  isElementDisableAtLevel(element, level) {
    // 地上は高度による要素の制限はない
    if (this.viewGround) return false;

    const elementName = this.keyMap[element]?.upperKey;
    if (elementName === undefined) return true;

    return this.lfmUpper.isElementDisableAtLevel(elementName, level)
  }
}

export default Lfm;
